import { observer } from "mobx-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useGlobalStore } from "../../../api/globalStores";
import Select from "../../../Common/Forms/Select";
import IconDelete from "../../../Common/Icons/IconDelete";
import IconImage from "../../../Common/Icons/IconImage";
import IconMic from "../../../Common/Icons/IconMic";
import IconSave from "../../../Common/Icons/IconSave";
import { FormState } from "../../state";
import AudioPlayer from "./AudioPlayerFull";
import EngagementPanelCheckbox from "./EngagementPanelCheckbox";
import EngagementPanelOption, { AnswerTextArea } from "./EngagementPanelOption";
import { UploadedImageCard } from "./UploadedImageCard";
import LockLogo from "../../../assets/LockLogo.png";
import { useState, useEffect } from "react";

export default observer(EngagementPanelEditor);

//TODO Consider making this a shared object
const EP_TYPE_TO_READABLE = {
  MCQ: "MCQ",
  FLASH_CARD: "Flash Card",
  POLL: "Poll",
} as const;

//TODO: Make delete button work
function EngagementPanelEditor() {
  const globalStores = useGlobalStore();
  const navigate = useNavigate();

  const [prevEPanel, setPrevEPanel] = useState<number | undefined>();

  // "Chope" the currentEditor slot if no one else is currently editing.
  useEffect(() => {
    const { currentPanelEditor: currentPanelEditorID, id: EPanelID } =
      globalStores.curEPanel.engagementPanel;
    const TeacherID = globalStores.teacherStore.teacherAccount.id;
    if (currentPanelEditorID === null || currentPanelEditorID === TeacherID) {
      const currentPanelBeingEdited = globalStores.curEPanel.engagementPanel.id;

      if (currentPanelEditorID === null) {
        globalStores.curEPanel.setCurrentPanelEditor(
          currentPanelBeingEdited,
          globalStores.teacherStore.teacherAccount.id
        );
      }

      if (currentPanelBeingEdited && prevEPanel !== currentPanelBeingEdited) {
        if (prevEPanel) {
          globalStores.curEPanel.setCurrentPanelEditor(prevEPanel, null);
        }
        if (prevEPanel !== EPanelID) {
          setPrevEPanel(EPanelID);
        }
      }
    } else if (
      typeof currentPanelEditorID === "number" &&
      currentPanelEditorID !== TeacherID
    ) {
      setPrevEPanel(undefined);
    }
  }, [
    globalStores.curEPanel.engagementPanel.currentPanelEditor,
    globalStores.curEPanel.engagementPanel.id,
  ]);

  useEffect(() => {
    const onBeforeUnload = () => {
      // run your cleanup code here
      if (
        globalStores.teacherStore.teacherAccount.id ===
        globalStores.curEPanel.engagementPanel.currentPanelEditor
      ) {
        globalStores.curEPanel.setCurrentPanelEditor(undefined, null);
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  const currentPanelEditor = globalStores.curEPanel.engagementPanel.currentPanelEditor;
  const teacherID = globalStores.teacherStore.teacherAccount.id;
  const isLockedByOther =
    typeof currentPanelEditor === "number" && currentPanelEditor !== teacherID;

  if (isLockedByOther) {
    return (
      <div className="p-6 bg-brij-Mud h-full w-full flex flex-col gap-[18px] flex-1 text-heading3 font-semibold text-brij-Default leading-[1.5] items-center justify-center">
        <img src={LockLogo} className="filter invert w-[30%]" />
        <p>Panel locked from editing</p>
        <p>Another teacher is currently editing this panel.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-brij-Mud h-full flex flex-col items-start justify-start gap-[18px] flex-1">
      {/* HEADER & SAVE */}
      <div className="flex flex-row justify-between h-[60px] w-full">
        <div className="gap-1 inline-flex flex-col items-start justify-start">
          <p className="text-heading3 font-semibold text-brij-Default inline leading-[1.5]">
            Edit Panel
          </p>
          <p className="text-small text-brij-Default inline leading-[1.4]">
            Changes saved automatically
          </p>
        </div>
        {/* SAVE BUTTON */}
        <button
          type="button"
          onClick={() => {
            toast.success("Saved Automatically");
          }}
          className="rounded-md pt-2.5 pb-3 gap-2.5 inline-flex flex-row items-center justify-center pl-[18px] pr-[18px]
          bg-brij-White hover:bg-brij-Default 
          text-brij-DarkGrey hover:text-brij-Black 
          fill-brij-DarkGrey hover:fill-brij-Black"
        >
          <div className="w-6 h-7">
            <IconSave />
          </div>{" "}
          <p className="text-lg font-medium text-center inline leading-[1.35]">
            Save
          </p>
        </button>
      </div>

      <div className="overflow-y-scroll flex flex-col items-start justify-start gap-[10px] w-full pr-6">
        {/* CHOOSE QUESTION TYPE */}
        <div className="gap-3 inline-flex flex-row items-center justify-start">
          <p className="text-normal text-brij-Default inline leading-[1.5]">
            Type:
          </p>
          <div className="w-38">
            <Select
              default={
                EP_TYPE_TO_READABLE[
                  globalStores.curEPanel.engagementPanel.type ?? "MCQ"
                ]
              }
              small
              placeholder="Pick Question Type"
              options={[
                {
                  value: "MCQ",
                  label: EP_TYPE_TO_READABLE["MCQ"],
                },
                {
                  value: "POLL",
                  label: EP_TYPE_TO_READABLE["POLL"],
                },
                {
                  value: "FLASH_CARD",
                  label: EP_TYPE_TO_READABLE["FLASH_CARD"],
                },
              ]}
              //@ts-ignore
              onChange={(type: FormState["type"]) => {
                globalStores.curEPanel.setType(type);
              }}
            />
          </div>
        </div>

        {/* CHOOSE QUESTION DURATION */}
        <>
          {(globalStores.curEPanel.engagementPanel.type === "MCQ" ||
            globalStores.curEPanel.engagementPanel.type === "POLL") && (
            <div className="gap-3 inline-flex flex-row items-center justify-start">
              <p className="text-normal text-brij-Default leading-[1.5]">
                Duration (seconds) :
              </p>
              <Select
                small
                icon="timer"
                placeholder="Duration"
                default={
                  globalStores.curEPanel.engagementPanel.duration
                    ? `${globalStores.curEPanel.engagementPanel.duration} seconds`
                    : undefined
                }
                options={[
                  { value: 10, label: "10 seconds" },
                  { value: 20, label: "20 seconds" },
                  { value: 30, label: "30 seconds" },
                  { value: 45, label: "45 seconds" },
                  { value: 60, label: "60 seconds" },
                ]}
                //@ts-ignore TODO: Specific type cast
                onChange={(duration: FormState["duration"]) => {
                  globalStores.curEPanel.setDuration(duration);
                }}
              />
            </div>
          )}
        </>

        {/* QUESTION TEXTAREA */}
        <p className="text-normal font-bold text-brij-Default inline leading-[1.5]">
          Question:
        </p>
        <div
          className={`flex items-center justify-center h-[157px] w-full ${
            // globalStores.curEPanel.assets.questionFile &&
            // globalStores.curEPanel.assets.questionFile.type.match("audio.*")
            //   ? "flex-col bg-brij-White rounded-xl"
            //   : "flex-row gap-[18px]"
            "flex-row gap-[18px]"
          }`}
        >
          {globalStores.curEPanel.assets.questionFile &&
            globalStores.curEPanel.assets.questionFile.type.match(
              "image.*"
            ) && (
              <div className="flex-1 rounded-xl border-solid border-2 border-brij-LightGrey bg-brij-White flex flex-row items-center overflow-clip h-[120px] justify-between p-2">
                <UploadedImageCard
                  file={globalStores.curEPanel.assets.questionFile}
                  handleFileChange={(value: FormState["questionFile"]) => {
                    globalStores.curEPanel.setQuestionAttachment(
                      value,
                      "INFER"
                    );
                  }}
                />
                <AnswerTextArea
                  defaultValue={
                    globalStores.curEPanel.engagementPanel.qnsAssetDesc ?? ""
                  }
                  handleOptionChange={(
                    value: FormState["questionAssetDescription"]
                  ) => {
                    globalStores.curEPanel.setQnsAssetDesc(value);
                  }}
                  placeholder={"Metatags"}
                  // value={globalStores.curEPanel.engagementPanel.qnsAssetDesc}
                />
              </div>
            )}
          {globalStores.curEPanel.assets.questionFile &&
            globalStores.curEPanel.assets.questionFile.type.match(
              "audio.*"
            ) && (
              <div className="flex flex-col flex-1 overflow-clip rounded-xl border-solid border-2 border-brij-LightGrey bg-brij-White items-start h-[150px] justify-between ">
                <AudioPlayer
                  file={globalStores.curEPanel.assets.questionFile}
                  handleFileChange={async (
                    value: FormState["questionFile"]
                  ) => {
                    await globalStores.curEPanel.setQuestionAttachment(
                      value,
                      "INFER"
                    );
                    window.location.reload();
                  }}
                />
                <AnswerTextArea
                  defaultValue={
                    globalStores.curEPanel.engagementPanel.qnsAssetDesc ?? ""
                  }
                  handleOptionChange={(
                    value: FormState["questionAssetDescription"]
                  ) => {
                    globalStores.curEPanel.setQnsAssetDesc(value);
                  }}
                  placeholder={"Metatags"}
                />
              </div>
            )}
          {/* UPLOAD IMAGE/AUDIO BUTTONS */}
          <>
            {!globalStores.curEPanel.assets.questionFile && (
              <div className="h-full inline-flex flex-col items-center justify-between w-[59px] gap-2">
                <label className="rounded-xl p-2.5 gap-1.5 inline-flex flex-col items-center justify-center h-[69px] bg-[#625958] hover:bg-[#524B4A] cursor-pointer w-full">
                  <div className="w-6 relative h-[23px]">
                    <div className="absolute left-0 right-[-4.17%] top-[6.26%] bottom-[-1.91%] fill-brij-White">
                      <IconImage />
                    </div>
                  </div>{" "}
                  <p className="text-brij-Neutral text-[10px] leading-[1.5]">
                    Image
                  </p>
                  <input
                    type="file"
                    id="question_file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e: any) => {
                      if (!e.target.files) return;
                      //TODO: Throw error for selecting more than one file
                      if (e.target.files.length !== 1) return;
                      globalStores.curEPanel.setQuestionAttachment(
                        e.target.files[0],
                        "INFER"
                      );
                    }}
                  />
                </label>
                <label
                  className={`${
                    globalStores.curEPanel.engagementPanel.type === "FLASH_CARD"
                      ? "opacity-40"
                      : "cursor-pointer hover:bg-[#524B4A]"
                  }   bg-[#625958] rounded-xl p-2.5 gap-1.5 inline-flex flex-col items-center justify-center h-[69px]  w-full`}
                >
                  <div className="h-6 w-[17px] fill-brij-White">
                    <IconMic />
                  </div>{" "}
                  <p className="text-brij-Neutral inline text-[10px] leading-[1.5]">
                    Audio
                  </p>
                  <input
                    disabled={
                      globalStores.curEPanel.engagementPanel.type ===
                      "FLASH_CARD"
                    }
                    type="file"
                    id="question_file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e: any) => {
                      if (!e.target.files) return;
                      //TODO: Throw error for selecting more than one file
                      if (e.target.files.length !== 1) return;
                      globalStores.curEPanel.setQuestionAttachment(
                        e.target.files[0],
                        "INFER"
                      );
                    }}
                  />
                </label>
              </div>
            )}
          </>

          {/* TEXT AREA */}
          <textarea
            placeholder={"Type your question here..."}
            defaultValue={globalStores.curEPanel.engagementPanel.qns ?? ""}
            className="font-ePanel h-full w-full text-xl font-light rounded-xl flex-1 border-solid border-4 p-6 border-brij-White focus:border-transparent box-border focus:outline-none resize-none focus:ring-0 bg-brij-White"
            onChange={(e) => {
              console.log(e.target.value);
              e.target.value === ""
                ? globalStores.curEPanel.setQuestionText("")
                : globalStores.curEPanel.setQuestionText(e.target.value);
            }}
          />
        </div>

        {/* ANSWER SECTION */}
        {(globalStores.curEPanel.engagementPanel.type === "MCQ" ||
          globalStores.curEPanel.engagementPanel.type === "POLL") && (
          <>
            <p className="text-normal font-bold text-brij-Default inline leading-[1.5]">
              Answers:
            </p>
            <div className="grid grid-cols-2 w-full gap-[18px] pb-6">
              <EngagementPanelOption
                type={globalStores.curEPanel.engagementPanel.type}
                optionFile={globalStores.curEPanel.assets.optionAFile}
                defaultValue={
                  globalStores.curEPanel.engagementPanel.optionA ?? ""
                }
                checked={globalStores.curEPanel.engagementPanel.answer === "A"}
                onClick={() => {
                  globalStores.curEPanel.setAnswer("A");
                }}
                handleOptionChange={(value: FormState["optionA"]) => {
                  value === ""
                    ? globalStores.curEPanel.setAnswerText(
                        "A",
                        "Enter Answer Text"
                      )
                    : globalStores.curEPanel.setAnswerText("A", value);
                }}
                handleOptionFileChange={(value: FormState["optionAFile"]) => {
                  //TODO: Make the ordering of variables a little more consistent
                  globalStores.curEPanel.setAnswerAttachment(
                    value,
                    "INFER",
                    "A"
                  );
                }}
              />
              <EngagementPanelOption
                type={globalStores.curEPanel.engagementPanel.type}
                defaultValue={
                  globalStores.curEPanel.engagementPanel.optionB ?? ""
                }
                optionFile={globalStores.curEPanel.assets.optionBFile}
                checked={globalStores.curEPanel.engagementPanel.answer === "B"}
                onClick={() => {
                  globalStores.curEPanel.setAnswer("B");
                }}
                handleOptionChange={(value: FormState["optionB"]) => {
                  value === ""
                    ? globalStores.curEPanel.setAnswerText(
                        "B",
                        "Enter Answer Text"
                      )
                    : globalStores.curEPanel.setAnswerText("B", value);
                }}
                handleOptionFileChange={(value: FormState["optionBFile"]) => {
                  //TODO: Make the ordering of variables a little more consistent
                  globalStores.curEPanel.setAnswerAttachment(
                    value,
                    "INFER",
                    "B"
                  );
                }}
              />
              <EngagementPanelOption
                type={globalStores.curEPanel.engagementPanel.type}
                optionFile={globalStores.curEPanel.assets.optionCFile}
                defaultValue={
                  globalStores.curEPanel.engagementPanel.optionC ?? ""
                }
                checked={globalStores.curEPanel.engagementPanel.answer === "C"}
                onClick={() => {
                  globalStores.curEPanel.setAnswer("C");
                }}
                handleOptionChange={(value: FormState["optionB"]) => {
                  value === ""
                    ? globalStores.curEPanel.setAnswerText(
                        "C",
                        "Enter Answer Text"
                      )
                    : globalStores.curEPanel.setAnswerText("C", value);
                }}
                handleOptionFileChange={(value: FormState["optionBFile"]) => {
                  //TODO: Make the ordering of variables a little more consistent
                  globalStores.curEPanel.setAnswerAttachment(
                    value,
                    "INFER",
                    "C"
                  );
                }}
              />

              <EngagementPanelOption
                type={globalStores.curEPanel.engagementPanel.type}
                defaultValue={
                  globalStores.curEPanel.engagementPanel.optionD ?? ""
                }
                optionFile={globalStores.curEPanel.assets.optionDFile}
                checked={globalStores.curEPanel.engagementPanel.answer === "D"}
                onClick={() => {
                  globalStores.curEPanel.setAnswer("D");
                }}
                handleOptionChange={(value: FormState["optionB"]) => {
                  value === ""
                    ? globalStores.curEPanel.setAnswerText(
                        "D",
                        "Enter Answer Text"
                      )
                    : globalStores.curEPanel.setAnswerText("D", value);
                }}
                handleOptionFileChange={(value: FormState["optionBFile"]) => {
                  //TODO: Make the ordering of variables a little more consistent
                  globalStores.curEPanel.setAnswerAttachment(
                    value,
                    "INFER",
                    "D"
                  );
                }}
              />
            </div>
          </>
        )}

        {/* CHECKBOX SECTION */}
        <div className="inline-flex flex-col items-start justify-start">
          <p className="text-normal text-brij-Default inline leading-[1.5]">
            Constant Components:
          </p>
          <div className="p-4 gap-4 flex flex-row items-start justify-start flex-wrap">
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.brijPinyin ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBrijStandard("brijPinyin", newVal);
              }}
              label="PinYin"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.brijGrammar ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBrijStandard("brijGrammar", newVal);
              }}
              label="Grammar"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.brijVocab ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBrijStandard("brijVocab", newVal);
              }}
              label="Vocab"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.brijRadicals ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBrijStandard("brijRadicals", newVal);
              }}
              label="Radicals"
            />
          </div>
        </div>
        <div className="inline-flex flex-col items-start justify-start">
          <p className="text-normal text-brij-Default inline leading-[1.5]">
            Bloom’s Taxonomy:
          </p>
          <div className="p-4 gap-4 flex flex-row items-start justify-start flex-wrap">
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.bloomRemembering ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBloomsStandard(
                  "bloomRemembering",
                  newVal
                );
              }}
              label="Remembering"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.bloomAnalyzing ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBloomsStandard(
                  "bloomAnalyzing",
                  newVal
                );
              }}
              label="Analyzing"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.bloomApplying ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBloomsStandard(
                  "bloomApplying",
                  newVal
                );
              }}
              label="Applying"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.bloomUnderstanding ??
                false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBloomsStandard(
                  "bloomUnderstanding",
                  newVal
                );
              }}
              label="Understanding"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.bloomEvaluating ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBloomsStandard(
                  "bloomEvaluating",
                  newVal
                );
              }}
              label="Evaluating"
            />
            <EngagementPanelCheckbox
              checked={
                globalStores.curEPanel.engagementPanel.bloomCreating ?? false
              }
              onChange={(newVal: boolean) => {
                globalStores.curEPanel.setBloomsStandard(
                  "bloomCreating",
                  newVal
                );
              }}
              label="Creating"
            />
          </div>
        </div>
        <div className="inline-flex items-start justify-between w-full">
          <div></div>
          <button
            type="button"
            onClick={() => {
              toast.promise(
                (async () => {
                  await globalStores.curEPanel.remove();
                  await globalStores.curModule.pullModuleDetails();
                  navigate(
                    `/teacher/epmaker?module=${globalStores.curModule.module.id}&epanel=${globalStores.curModule.engagementPanels[0]?.id}`,
                    {}
                  );
                })(),
                {
                  success: "Engagement Panel Removed",
                  error: (e) => e.message,
                  loading: "Removing Engagement Panel",
                }
              );
            }}
            className="rounded-md py-2 gap-2.5 inline-flex flex-row items-center justify-center pl-[18px] pr-[18px] bg-brij-Red  hover:opacity-80"
          >
            <div className="w-6 h-7 stroke-brij-White">
              <IconDelete />
            </div>{" "}
            <p className="text-lg text-brij-White font-medium text-center inline leading-[1.35]">
              Delete
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
